import React, { useState, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Platform,
  TextInput,
  ActivityIndicator,
} from 'react-native';
import DropDownPicker from 'react-native-dropdown-picker';
import { DLODropdownOption } from '../../types/dlo.types';
import { DayItem } from '../DLOFilterByDayModal';
import DLOTooltip from '../DLOTooltip';

export interface EmployeeRowData {
  id: string;
  isSelected: boolean;
  employee: DLODropdownOption | null;
  role: DLODropdownOption | null;
  shift: DLODropdownOption | null;
  sunday: string;
  monday: string;
  tuesday: string;
  wednesday: string;
  thursday: string;
  friday: string;
  saturday: string;
  total: string;
  // Split role functionality
  isSplit?: boolean;
  parentId?: string;
  childId?: string;
  isChildRow?: boolean;
  isLocked?: boolean;
  // Validation state for dropdown error highlighting
  showRoleError?: boolean;
  showShiftError?: boolean;
  // Set to true when the row's role is disabled/removed in the current system (e.g. copied from old schedule)
  isRoleInvalid?: boolean;
  // Snapshot of backend identity/hours used for zero-hour delete payload entries.
  // This remains stable even if user edits employee/role/shift in the UI.
  sourceSnapshot?: {
    employee: DLODropdownOption | null;
    role: DLODropdownOption | null;
    shift: DLODropdownOption | null;
    sunday: string;
    monday: string;
    tuesday: string;
    wednesday: string;
    thursday: string;
    friday: string;
    saturday: string;
  };
}

export interface DLOCreateScheduleEmployeeRowProps {
  data: EmployeeRowData;
  onUpdate: (id: string, updatedData: Partial<EmployeeRowData>) => void;
  onDelete?: (id: string) => void;
  employeeOptions: DLODropdownOption[];
  roleOptions: DLODropdownOption[];
  shiftOptions: DLODropdownOption[];
  selectedDays: string[];
  dayFilter?: DayItem[];
  // All rows data for calculating totals including child split rows
  allEmployeeRows?: EmployeeRowData[];
  // Function to update multiple rows (for parent checkbox selecting all children)
  onUpdateMultiple?: (updates: Array<{ id: string; updatedData: Partial<EmployeeRowData> }>) => void;
  // Global dropdown management props
  isEmployeeDropdownOpen?: boolean;
  isRoleDropdownOpen?: boolean;
  isShiftDropdownOpen?: boolean;
  onDropdownOpen?: (rowId: string, type: 'employee' | 'role' | 'shift', isOpen: boolean) => void;
  // Loading state props
  isEmployeeLoading?: boolean;
  isRoleLoading?: boolean;
  isShiftLoading?: boolean;
  // Checkbox state
  isCheckboxDisabled?: boolean;
  // Input field control for week cutoff
  inputsEnabled?: boolean;
  // Validation callback for save button state
  onValidationChange?: (rowId: string, isValid: boolean) => void;
}

const DLOCreateScheduleEmployeeRow: React.FC<DLOCreateScheduleEmployeeRowProps> = ({
  data,
  onUpdate,
  onDelete,
  employeeOptions,
  roleOptions,
  shiftOptions,
  selectedDays,
  dayFilter = [],
  // All rows data for calculating totals including child split rows
  allEmployeeRows = [],
  // Function to update multiple rows (for parent checkbox selecting all children)
  onUpdateMultiple,
  // Global dropdown management props
  isEmployeeDropdownOpen = false,
  isRoleDropdownOpen = false,
  isShiftDropdownOpen = false,
  onDropdownOpen,
  // Loading state props
  isEmployeeLoading = false,
  isRoleLoading = false,
  isShiftLoading = false,
  // Checkbox state
  isCheckboxDisabled = false,
  // Input field control for week cutoff
  inputsEnabled = true,
  // Validation callback for save button state
  onValidationChange,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  // Use global dropdown state instead of local state
  const employeeDropdownOpen = isEmployeeDropdownOpen;
  const roleDropdownOpen = isRoleDropdownOpen;
  const shiftDropdownOpen = isShiftDropdownOpen;

  // Notify parent component when validation state changes
  useEffect(() => {
    if (onValidationChange) {
      const isValid = isRowComplete();
      onValidationChange(data.id, isValid);
    }
  }, [data.employee, data.role, data.shift, onValidationChange, data.id]);

  // Check if a day is disabled based on day filter
  const isDayDisabled = (dayName: string): boolean => {
    const dayItem = dayFilter.find(item => item.name.toLowerCase() === dayName.toLowerCase());
    return dayItem ? !dayItem.enabled : false;
  };

  // Validation functions for dropdown error highlighting
  const shouldShowRoleError = (): boolean => {
    // Show role error if employee is selected but role is not selected
    if (data.employee && !data.role) return true;
    // Show role error if the role is disabled/removed (invalid) in the current system
    if (data.role && data.isRoleInvalid) return true;
    return false;
  };

  const shouldShowShiftError = (): boolean => {
    // Show shift error if employee and role are selected but shift is not selected
    return !!(data.employee && data.role && !data.shift);
  };

   const shouldShowEmployeeError = (): boolean => {
    // Show employee error if shift has value and employee is null
    // OR if employee exists but its ID doesn't match any available employee options
    return (data.employee === null && data.shift !== null) ||
           (data.employee !== null && !employeeOptions.some(emp => emp.id === data.employee?.id));
  };
  // Validation function to check if row has complete selections
  const isRowComplete = (): boolean => {
    // Row is invalid if role is disabled/removed in the current system
    if (data.isRoleInvalid) return false;
    // Row is complete if it has no employee (empty row) or if it has all three selections
    return !data.employee || (!!data.employee && !!data.role && !!data.shift);
  };

  // Validation function to check if row has any incomplete selections
  const hasIncompleteSelections = (): boolean => {
    return shouldShowRoleError() || shouldShowShiftError();
  };

  // Custom render function for dropdown items with styled label and GPID
  const renderDropdownItem = (option: DLODropdownOption) => (
    <View style={styles.dropdownItemContainer}>
      <Text style={styles.dropdownItemLabel}>{option.label}</Text>
      {option.gpid && (
        <Text style={styles.dropdownItemGpid}>{option.gpid}</Text>
      )}
    </View>
  );

  // Transform DLODropdownOption to DropDownPicker format
  const transformOptions = (options: DLODropdownOption[]) =>
    options.map(option => ({
      label: option.gpid ? `${option.label} ${option.gpid}` : option.label,
      value: option.id,
      key: option.id,
      custom: option, // Store the original option for custom rendering
    }));

  // Memoize transformed options so DropDownPicker Picker gets a stable array
  // reference and doesn't trigger its internal useEffect → setState infinite loop.
  const transformedEmployeeOptions = useMemo(
    () => transformOptions(employeeOptions),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [employeeOptions]
  );
  const transformedRoleOptions = useMemo(
    () => transformOptions(roleOptions),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [roleOptions]
  );
  const transformedShiftOptions = useMemo(
    () => transformOptions(shiftOptions),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shiftOptions]
  );

  // Render loading content for dropdowns
  const renderLoadingContent = () => (
    <View style={styles.loadingContainer}>
      <ActivityIndicator size="small" color="#005CBC" />
      <Text style={styles.loadingText}>Loading...</Text>
    </View>
  );

  // Functions to handle opening dropdowns using global state management
  const handleEmployeeDropdownOpen = (value: boolean | ((prev: boolean) => boolean)) => {
    const newValue = typeof value === 'function' ? value(employeeDropdownOpen) : value;
    onDropdownOpen?.(data.id, 'employee', newValue);
  };

  const handleRoleDropdownOpen = (value: boolean | ((prev: boolean) => boolean)) => {
    const newValue = typeof value === 'function' ? value(roleDropdownOpen) : value;
    onDropdownOpen?.(data.id, 'role', newValue);
  };

  const handleShiftDropdownOpen = (value: boolean | ((prev: boolean) => boolean)) => {
    const newValue = typeof value === 'function' ? value(shiftDropdownOpen) : value;
    onDropdownOpen?.(data.id, 'shift', newValue);
  };

  const handleOutsidePress = () => {
    onDropdownOpen?.(data.id, 'employee', false);
    onDropdownOpen?.(data.id, 'role', false);
    onDropdownOpen?.(data.id, 'shift', false);
  };

  const handleCheckboxPress = () => {
    if (!isCheckboxDisabled) {
      const newSelectedState = !data.isSelected;
      
      // Update the parent row's selection state
      onUpdate(data.id, { isSelected: newSelectedState });
      
      // If this is a parent row with child split roles, update all children as well
      if (!data.isChildRow && data.isSplit && allEmployeeRows.length > 0 && onUpdateMultiple) {
        const childRows = allEmployeeRows.filter(row => row.parentId === data.id);
        
        if (childRows.length > 0) {
          const childUpdates = childRows.map(childRow => ({
            id: childRow.id,
            updatedData: { isSelected: newSelectedState }
          }));
          
          onUpdateMultiple(childUpdates);
        }
      }
    }
  };

  // Validation logic for sequential dropdown enabling
  const isEmployeeDropdownEnabled = (): boolean => {
    // Always enable if data is prepopulated (existing employee row)
    if (data.employee || data.role || data.shift) {
      return inputsEnabled;
    }
    // For new rows, employee dropdown is always enabled first
    return inputsEnabled;
  };

  const isRoleDropdownEnabled = (): boolean => {
    // Always enable if data is prepopulated (existing employee row)
    if (data.employee && data.role) {
      return inputsEnabled;
    }
    // For new rows, only enable if employee is selected
    return inputsEnabled && !!data.employee;
  };

  const isShiftDropdownEnabled = (): boolean => {
    // Always enable if data is prepopulated (existing employee row)
    if (data.employee && data.role && data.shift) {
      return inputsEnabled;
    }
    // For new rows, only enable if both employee and role are selected
    return inputsEnabled && !!data.employee && !!data.role;
  };

  const handleEmployeeSelect = (value: string | null) => {
    const selectedOption = value ? (employeeOptions.find(emp => emp.id === value) || null) : null;
    // When employee changes, set the employee object (not just id) and clear role/shift for new rows
    const updates: Partial<EmployeeRowData> = { employee: selectedOption };

    // If this was a new row without prepopulated data, clear dependent fields
    if (!data.employee && !data.role && !data.shift) {
      updates.role = null;
      updates.shift = null;
    }
    
    onUpdate(data.id, updates);
  };

  const handleRoleSelect = (value: string | null) => {
    const selectedOption = value ? roleOptions.find(opt => opt.id === value) || null : null;
    // When role changes, clear shift if this is a new row
    const updates: Partial<EmployeeRowData> = { role: selectedOption, isRoleInvalid: false };
    
    // If this was a new row without prepopulated data, clear dependent fields
    if (!data.shift || (!data.employee && !data.role && !data.shift)) {
      updates.shift = null;
    }
    
    onUpdate(data.id, updates);
  };

  const handleShiftSelect = (value: string | null) => {
    const selectedOption = value ? shiftOptions.find(opt => opt.id === value) || null : null;
    onUpdate(data.id, { shift: selectedOption });
  };

  const handleHourChange = (day: string, value: string) => {
    // Only allow numbers (including decimals)
    const numericValue = value.replace(/[^0-9.]/g, '');
    onUpdate(data.id, { [day]: numericValue });
  };

  // Validation functions for max hours checking
  
  // Helper function to extract shift data from dropdown value
  const getShiftData = (shift: DLODropdownOption | null): { id: string; shift_type: string; time_format: string; time_value: string } | null => {
    if (!shift || !shift.value) return null;
    
    try {
      // If value is JSON, parse it
      return JSON.parse(shift.value);
    } catch (error) {
      // If parsing fails, assume it's the old format (just ID)
      console.warn('Could not parse shift value as JSON, using fallback:', shift.value);
      return {
        id: shift.id,
        shift_type: '',
        time_format: '',
        time_value: ''
      };
    }
  };
  
  const getShiftMaxHours = (): number => {
    const shiftData = getShiftData(data.shift);
    if (shiftData && shiftData.shift_type) {
      const shiftType = shiftData.shift_type.toLowerCase();
      if (shiftType.includes('5x8')) return 8;
      if (shiftType.includes('4x10')) return 10;
    }
    
    // Fallback to checking label if shift_type is not available
    const shiftLabel = data.shift?.label?.toLowerCase() || '';
    if (shiftLabel.includes('5x8')) return 8;
    if (shiftLabel.includes('4x10')) return 10;
    
    return 12; // Default max hours per day
  };

  const getDayHours = (day: string): number => {
    const dayValues: { [key: string]: string } = {
      sunday: data.sunday,
      monday: data.monday,
      tuesday: data.tuesday,
      wednesday: data.wednesday,
      thursday: data.thursday,
      friday: data.friday,
      saturday: data.saturday
    };
    return parseFloat(dayValues[day] || '0') || 0;
  };

  const getWeeklyHours = (): number => {
    const weekdayValues = [
      data.sunday,
      data.monday,
      data.tuesday,
      data.wednesday,
      data.thursday,
      data.friday,
      data.saturday
    ];

    let total = weekdayValues.reduce((sum, value) => {
      const numericValue = parseFloat(value) || 0;
      return sum + numericValue;
    }, 0);

    // If this is a parent row (not a child row and has split roles), 
    // add the hours from all child split rows
    if (!data.isChildRow && data.isSplit && allEmployeeRows.length > 0) {
      const childRows = allEmployeeRows.filter(row => row.parentId === data.id);
      
      childRows.forEach(childRow => {
        const childWeekdayValues = [
          childRow.sunday,
          childRow.monday,
          childRow.tuesday,
          childRow.wednesday,
          childRow.thursday,
          childRow.friday,
          childRow.saturday
        ];
        
        const childTotal = childWeekdayValues.reduce((sum, value) => {
          const numericValue = parseFloat(value) || 0;
          return sum + numericValue;
        }, 0);
        
        total += childTotal;
      });
    }

    return total;
  };

  const isDayHoursExceeded = (day: string): boolean => {
    const dayHours = getDayHours(day);
    const maxHours = getShiftMaxHours();
    return dayHours > maxHours;
  };

  const isDailyMaxExceeded = (day: string): boolean => {
    const dayHours = getDayHours(day);
    return dayHours > 12; // General daily max is 12 hours
  };

  const isWeeklyMaxExceeded = (): boolean => {
    const weeklyHours = getWeeklyHours();
    return weeklyHours > 60; // Weekly max is 60 hours
  };

  const isWeeklyHighlightThreshold = (): boolean => {
    const weeklyHours = getWeeklyHours();
    return weeklyHours > 40; // Highlight after 40 hours
  };

  const getValidationMessage = (day: string): string => {
    if (day === 'total') {
      if (isWeeklyMaxExceeded()) {
        return 'Weekly hours exceeded maximum of 60 hours';
      }
      if (isWeeklyHighlightThreshold()) {
        return 'Weekly hours exceed 40 hours';
      }
      return '';
    }

    const dayHours = getDayHours(day);
    const maxHours = getShiftMaxHours();
    
    if (isDailyMaxExceeded(day)) {
      return 'Daily hours exceeded maximum of 12 hours';
    }
    
    if (isDayHoursExceeded(day)) {
      const shiftLabel = data.shift?.label || 'shift';
      return `Daily hours exceeded ${shiftLabel} maximum of ${maxHours} hours`;
    }
    
    return '';
  };

  const shouldHighlightCell = (day: string): boolean => {
    if (day === 'total') {
      return isWeeklyMaxExceeded() || isWeeklyHighlightThreshold();
    }
    return isDailyMaxExceeded(day) || isDayHoursExceeded(day);
  };

  // Calculate total hours from all weekdays
  const calculateTotalHours = (): string => {
    // Only sum enabled days from dayFilter
    const dayNames = [
      'sunday',
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday'
    ];
        
    // If no dayFilter provided, fallback to all days enabled
    const enabledDays = dayFilter && dayFilter.length > 0
      ? dayFilter.filter(day => day.enabled).map(day => day.name.toLowerCase())
      : dayNames;

    // Sum only enabled days for this row
    let total = dayNames.reduce((sum, day) => {
      if (enabledDays.includes(day)) {
        const value = parseFloat((data as any)[day] || '0') || 0;
        return sum + value;
      }
      return sum;
        }, 0);
        
    // If this is a parent row (not a child row and has split roles), add the enabled days from all child split rows
    if (!data.isChildRow && data.isSplit && allEmployeeRows.length > 0) {
      const childRows = allEmployeeRows.filter(row => row.parentId === data.id && row.isChildRow);
      childRows.forEach(child => {
        total += dayNames.reduce((childSum, day) => {
          if (enabledDays.includes(day)) {
            const value = parseFloat((child as any)[day] || '0') || 0;
            return childSum + value;
          }
          return childSum;
          }, 0);
      });
    }
          
    return total.toFixed(2);
  };

  const renderCheckbox = () => (
    <TouchableOpacity 
      style={styles.checkboxContainer}
      onPress={handleCheckboxPress}
      activeOpacity={isCheckboxDisabled ? 1 : 0.7}
      disabled={isCheckboxDisabled}
    >
      <View style={[
        styles.checkbox,
        data.isSelected && styles.checkboxSelected,
        isCheckboxDisabled && styles.checkboxDisabled
      ]}>
        {data.isSelected && (
          <Text style={styles.checkboxCheck}>✓</Text>
        )}
      </View>
    </TouchableOpacity>
  );

  const getDayAbbreviation = (day: string): string => {
    switch (day.toLowerCase()) {
      case 'sunday': return 'sun';
      case 'monday': return 'mon';
      case 'tuesday': return 'tue';
      case 'wednesday': return 'wed';
      case 'thursday': return 'thu';
      case 'friday': return 'fri';
      case 'saturday': return 'sat';
      default: return day;
    }
  };

  const isHighlighted = (day: string): boolean => {
    if (!data.isSelected || day === 'total') return false;
    const dayAbbr = getDayAbbreviation(day);
    return selectedDays.includes(dayAbbr);
  };

  const hasSelectedDays = (): boolean => {
    return data.isSelected && selectedDays.length > 0;
  };

  const renderHourInput = (value: string, day: string, width?: number, readOnly: boolean = false) => {
    const hasValidationError = shouldHighlightCell(day);
    const validationMessage = getValidationMessage(day);

  return (
    <View style={[
        day === 'total' ? styles.totalInputWrapper : [
          styles.inputWrapper,
          width !== undefined ? { width } : {}
        ],
        isHighlighted(day) && styles.inputWrapperHighlighted
      ]}>
        <TextInput
          style={[
            day === 'total' ? styles.totalHourInput : styles.hourInput, 
            width !== undefined && day !== 'total' ? { width: (width - 16) } : {}, // Only apply width calculation for non-total inputs
            day === 'total' ? { width: 76, textAlign: 'center' } : {}, // Fixed width for total inputs with center alignment and right margin - use ternary for more explicit condition
            (readOnly || !inputsEnabled) && day !== 'total' && styles.hourInputDisabled,
            isHighlighted(day) && styles.hourInputHighlighted,
            hasValidationError && styles.hourInputValidationError
          ]}
          value={value}
          onChangeText={(readOnly || !inputsEnabled) ? undefined : (text) => handleHourChange(day, text)}
          placeholder=""
          keyboardType="numeric"
          textAlign="center"
          editable={!readOnly && inputsEnabled}
          {...(Platform.OS === 'web' && {
            inputMode: 'decimal' as any,
            pattern: '[0-9]*',
            title: hasValidationError ? validationMessage : undefined
          })}
        />
        {hasValidationError && validationMessage && Platform.OS !== 'web' && (
        <TouchableOpacity 
            style={styles.validationIcon}
            onPress={() => alert(validationMessage)}
          >
            <Text style={styles.validationIconText}>!</Text>
          </TouchableOpacity>
            )}
          </View>
    );
  };

  return (
    <View 
      style={[
        styles.container,
        isHovered && styles.containerHovered,
        data.isSelected && styles.containerSelected,
        hasSelectedDays() && styles.containerHighlighted
      ]}
      {...(Platform.OS === 'web' && {
        onMouseEnter: () => setIsHovered(true),
        onMouseLeave: () => setIsHovered(false),
      } as any)}
    >
      {/* Checkbox Section */}
      <View style={styles.checkboxSection}>
        {renderCheckbox()}
      </View>
      <View style={styles.divider} />

      {/* Employee Dropdown */}
      <View style={[
        styles.dropdownSection,
        data.isSelected && styles.dropdownSectionSelected,
        hasSelectedDays() && styles.dropdownSectionHighlighted
      ]}>
        {isEmployeeLoading ? (
          renderLoadingContent()
        ) : (
          <DropDownPicker
            open={employeeDropdownOpen}
            value={data.employee?.id || null}
            items={transformedEmployeeOptions}
            setOpen={handleEmployeeDropdownOpen}
            setValue={(callback) => {
              const newValue = typeof callback === 'function' ? callback(data.employee?.id || null) : callback;
              handleEmployeeSelect(newValue);
            }}
            placeholder="Select Employee"
            disabled={!isEmployeeDropdownEnabled()}
            style={[
              styles.dropdownStyle,
              data.isSelected && styles.dropdownStyleSelected,
              hasSelectedDays() && styles.dropdownStyleHighlighted,
              !isEmployeeDropdownEnabled() && styles.dropdownStyleDisabled,
              shouldShowEmployeeError() && styles.dropdownStyleError
            ]}
            dropDownContainerStyle={styles.dropdownContainer}
            textStyle={styles.dropdownText}
            placeholderStyle={styles.dropdownPlaceholder}
            listItemLabelStyle={styles.dropdownListItemLabel}
            listItemContainerStyle={styles.dropdownListItemContainer}
            searchContainerStyle={styles.dropdownSearchContainer}
            searchTextInputStyle={styles.dropdownSearchInput}
            searchPlaceholder=""
            maxHeight={200}
            zIndex={3000}
            zIndexInverse={1000}
            searchable={true}
            closeAfterSelecting={true}
            dropDownDirection='TOP'
          />
        )}
      </View>
      <View style={styles.divider} />

      {/* Role Dropdown */}
      <View style={[
        styles.roleSection,
        data.isSelected && styles.dropdownSectionSelected,
        hasSelectedDays() && styles.dropdownSectionHighlighted
      ]}>
        {isRoleLoading ? (
          renderLoadingContent()
        ) : (
          <DropDownPicker
            open={roleDropdownOpen}
            value={data.role?.id || null}
            items={transformedRoleOptions}
            setOpen={handleRoleDropdownOpen}
            setValue={(callback) => {
              const newValue = typeof callback === 'function' ? callback(data.role?.id || null) : callback;
              handleRoleSelect(newValue);
            }}
            placeholder="Select Role"
            disabled={!isRoleDropdownEnabled()}
            style={[
              styles.dropdownStyle,
              data.isSelected && styles.dropdownStyleSelected,
              hasSelectedDays() && styles.dropdownStyleHighlighted,
              !isRoleDropdownEnabled() && styles.dropdownStyleDisabled,
              shouldShowRoleError() && styles.dropdownStyleError
            ]}
            dropDownContainerStyle={styles.dropdownContainer}
            textStyle={styles.dropdownText}
            placeholderStyle={styles.dropdownPlaceholder}
            listItemLabelStyle={styles.dropdownListItemLabel}
            listItemContainerStyle={styles.dropdownListItemContainer}
            searchContainerStyle={styles.dropdownSearchContainer}
            searchTextInputStyle={styles.dropdownSearchInput}
            searchPlaceholder=""
            maxHeight={200}
            zIndex={2000}
            zIndexInverse={2000}
            searchable={true}
            closeAfterSelecting={true}
            dropDownDirection='TOP'
          />
        )}
      </View>
      <View style={styles.divider} />

      {/* Shift Dropdown */}
      <View style={[
        styles.shiftSection,
        data.isSelected && styles.dropdownSectionSelected,
        hasSelectedDays() && styles.dropdownSectionHighlighted
      ]}>
        {isShiftLoading ? (
          renderLoadingContent()
        ) : (
          <DropDownPicker
            open={shiftDropdownOpen}
            value={data.shift?.id || null}
            items={transformedShiftOptions}
            setOpen={handleShiftDropdownOpen}
            setValue={(callback) => {
              const newValue = typeof callback === 'function' ? callback(data.shift?.id || null) : callback;
              handleShiftSelect(newValue);
            }}
            placeholder="Select Shift"
            disabled={!isShiftDropdownEnabled()}
            style={[
              styles.dropdownStyle,
              data.isSelected && styles.dropdownStyleSelected,
              hasSelectedDays() && styles.dropdownStyleHighlighted,
              !isShiftDropdownEnabled() && styles.dropdownStyleDisabled,
              shouldShowShiftError() && styles.dropdownStyleError
            ]}
            dropDownContainerStyle={styles.dropdownContainer}
            textStyle={styles.dropdownText}
            placeholderStyle={styles.dropdownPlaceholder}
            listItemLabelStyle={styles.dropdownListItemLabel}
            listItemContainerStyle={styles.dropdownListItemContainer}
            searchContainerStyle={styles.dropdownSearchContainer}
            searchTextInputStyle={styles.dropdownSearchInput}
            searchPlaceholder=""
            maxHeight={200}
            zIndex={1000}
            zIndexInverse={3000}
            searchable={true}
            closeAfterSelecting={true}
            dropDownDirection='TOP'
          />
        )}
      </View>

      {/* Hour Input Sections */}
      <View style={styles.hoursContainer}>
      <View style={styles.divider} />
        {renderHourInput(data.sunday, 'sunday', 92, isDayDisabled('sunday'))}
            <View style={styles.divider} />
        {renderHourInput(data.monday, 'monday', 92, isDayDisabled('monday'))}
      <View style={styles.divider} />
        {renderHourInput(data.tuesday, 'tuesday', 92, isDayDisabled('tuesday'))}
        <View style={styles.divider} />
        {renderHourInput(data.wednesday, 'wednesday', 92, isDayDisabled('wednesday'))}
        <View style={styles.divider} />
        {renderHourInput(data.thursday, 'thursday', 92, isDayDisabled('thursday'))}
        <View style={styles.divider} />
        {renderHourInput(data.friday, 'friday', 92, isDayDisabled('friday'))}
        <View style={styles.divider} />
        {renderHourInput(data.saturday, 'saturday', 92, isDayDisabled('saturday'))}
        <View style={styles.divider} />
        {renderHourInput(calculateTotalHours(), 'total', 204, true)}
      </View>
    </View>
  );
};

// Helper function for parent components to validate all employee rows
export const validateEmployeeRows = (rows: EmployeeRowData[]): boolean => {
  return rows.every(row => {
    // Check for employee error (shift selected but no employee)
    const hasEmployeeError = row.employee === null && row.shift !== null;
    // Row is valid if it has no employee (empty row) or if it has all three selections and no employee error
    return (!row.employee || (!!row.employee && !!row.role && !!row.shift)) && !hasEmployeeError;
  });
};

// Helper function to check if any rows have incomplete selections
export const hasIncompleteEmployeeRows = (rows: EmployeeRowData[]): boolean => {
  return rows.some(row => {
    // Check for employee error (shift selected but no employee)
    const hasEmployeeError = row.employee === null && row.shift !== null;
    if (hasEmployeeError) return true;

    // Check for invalid role (disabled/removed role copied from old schedule)
    if (row.isRoleInvalid) return true;

    if (!row.employee) return false; // Empty rows are not incomplete
    return !row.role || !row.shift; // Has employee but missing role or shift
  });
};

const styles = StyleSheet.create({
  container: {
    width: 1416,
    height: 40,
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'stretch',
    borderWidth: 1,
    borderTopWidth: 0,
    borderColor: '#DFDFDF',
    backgroundColor: '#FBFCFE',
    position: 'relative',
  },
  containerHovered: {
    backgroundColor: '#F8F9FA',
  },
  containerSelected: {
    backgroundColor: '#E0F1FF',
  },
  containerHighlighted: {
    backgroundColor: '#EFF5FC',
  },
  checkboxSection: {
    width: 43,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 16,
  },
  checkboxContainer: {
    width: 18,
    height: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkbox: {
    width: 12,
    height: 12,
    borderWidth: 1,
    borderColor: '#3A3A3A',
    borderRadius: 2,
    backgroundColor: 'transparent',
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkboxSelected: {
    backgroundColor: '#005CBC',
    borderColor: '#005CBC',
  },
  checkboxDisabled: {
    backgroundColor: '#F5F5F5',
    borderColor: '#C0C0C0',
    opacity: 0.6,
  },
  checkboxCheck: {
    color: '#FFFFFF',
    fontSize: 8,
    fontWeight: 'bold',
  },
  dropdownSection: {
    width: 240,
    height: 40,
    justifyContent: 'center',
    paddingVertical: 5,
    paddingHorizontal: 10,
  },
  roleSection: {
    width: 164,
    height: 40,
    justifyContent: 'center',
    paddingVertical: 5,
    paddingHorizontal: 10,
  },
  shiftSection: {
    width: 164,
    height: 40,
    justifyContent: 'center',
    paddingVertical: 10,
    paddingHorizontal: 10,
  },
  dropdownSectionSelected: {
    backgroundColor: '#E0F1FF',
  },
  dropdownSectionHighlighted: {
    backgroundColor: '#EFF5FC',
    borderRadius: 4,
  },
  hoursContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    height: 40,
  },
  inputWrapper: {
    width: 92, // Match the width passed to renderHourInput
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 10,
  },
  inputWrapperHighlighted: {
    width: 92,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 10,
    backgroundColor: '#E0F1FF',
  },
  totalColumnContainer: {
    width: 204, // Full column width
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'row',
  },
  totalInputWrapper: {
    width: 204, // Full column width like totalColumnContainer
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
   // paddingRight: 50, // Use padding to ensure the input is positioned correctly within the wrapper
  },
  hourInput: {
    height: 24,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#C0C0C0',
    backgroundColor: '#FBFCFE',
    color: '#3A3A3A',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 18,
    paddingHorizontal: 8,
    minHeight: 24,
    textAlign: 'center',
  },
  hourInputDisabled: {
    backgroundColor: '#F5F5F5',
    borderColor: '#D3D3D3',
    color: '#999999',
    opacity: 0.8,
    width: 76
  },
  hourInputHighlighted: {
    backgroundColor: '#E0F1FF',
  },
  hourInputValidationError: {
    borderColor: '#FFD700', // Yellow border for validation errors
    borderWidth: 2,
    backgroundColor: '#FFFACD', // Light yellow background
  },
  validationIcon: {
    position: 'absolute',
    right: 2,
    top: 2,
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: '#FFD700',
    justifyContent: 'center',
    alignItems: 'center',
  },
  validationIconText: {
    color: '#B8860B',
    fontSize: 10,
    fontWeight: 'bold',
  },
  divider: {
    width: 1,
    height: 40,
    backgroundColor: '#DFDFDF',
  },
  dropdownStyle: {
    borderColor: '#C0C0C0',
    borderWidth: 1,
    borderRadius: 4,
    backgroundColor: '#FBFCFE',
    minHeight: 28,
    height: 28,
  },
  dropdownStyleSelected: {
    backgroundColor: '#E0F1FF',
  },
  dropdownStyleHighlighted: {
    backgroundColor: '#EFF5FC',
  },
  dropdownStyleDisabled: {
    backgroundColor: '#F5F5F5',
    borderColor: '#D3D3D3',
    opacity: 0.7,
  },
  dropdownStyleError: {
    borderColor: '#FF4444',
    borderWidth: 2,
  },
  dropdownContainer: {
    borderColor: '#C0C0C0',
    borderWidth: 1,
    borderRadius: 4,
    backgroundColor: '#FBFCFE',
    maxHeight: 200,
  },
  dropdownText: {
    color: '#3A3A3A',
    fontSize: 12,
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
  dropdownPlaceholder: {
    color: '#6C757D',
    fontSize: 12,
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
  dropdownListItemLabel: {
    color: '#3A3A3A',
    fontSize: 12,
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
  dropdownListItemContainer: {
    height: 30,
  },
  dropdownSearchContainer: {
    borderBottomWidth: 1,
    borderBottomColor: '#C0C0C0',
    backgroundColor: '#FBFCFE',
    paddingHorizontal: 6,
    paddingVertical: 4,
    alignItems: 'stretch',
  },
  dropdownSearchInput: {
    color: '#3A3A3A',
    fontSize: 12,
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    borderWidth: 1,
    borderColor: '#C0C0C0',
    borderRadius: 4,
    paddingHorizontal: 6,
    paddingVertical: 4,
    backgroundColor: '#FBFCFE',
    height: 24,
    flex: 1,
    minWidth: 0,
  },
  loadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
  },
  loadingText: {
    marginLeft: 8,
    color: '#6C757D',
    fontSize: 12,
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
  // Specific style for Total Hours to ensure consistent text color
  totalHourInput: {
    height: 24,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#C0C0C0',
    backgroundColor: '#F5F5F5',
    color: '#3A3A3A', // Always rgb(58, 58, 58)
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 18,
    minHeight: 24,
    textAlign: 'center',
    width: 76,
    marginRight: 50,
  },
  // Custom dropdown item styles
  dropdownItemContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
  },
  dropdownItemLabel: {
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontWeight: '400',
    fontSize: 14, // XSmall
    lineHeight: 18, // XSmall line height
    letterSpacing: 0,
    color: '#2D2D2D',
  },
  dropdownItemGpid: {
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontWeight: '400',
    fontSize: 12, // XXSmall
    lineHeight: 16, // XXSmall line height
    letterSpacing: 0,
    color: '#838383',
    marginLeft: 6,
  },
});

export default DLOCreateScheduleEmployeeRow;
